import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { uploadFile, type UploadedFile } from "@client/lib/uploads"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"
import { DatePicker } from "./components/date-picker"
import { TimePicker } from "./components/time-picker"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import { ContentPagination } from "./content-components"
import { ProgramsPageSkeleton } from "./components/skeleton"

type ProgramIcon = "book" | "scissor" | "atom"
type ProgramStatus = "active" | "inactive"
type ParticipantStatus = "active" | "inactive" | "graduated"
const PROGRAM_RECORD_PAGE_SIZE = 10

interface ProgramListItem {
  id: string
  code: string
  name: string
  description: string | null
  icon: ProgramIcon
  status: ProgramStatus
  participantsCount: number
  eventParticipantsCount: number
}

interface EventAttendanceItem {
  id: string
  eventId: string
  eventCode: string
  eventTitle: string
  eventDate: string
  firstName: string
  lastName: string
  age: number | null
  gender: string | null
  email: string | null
  phone: string | null
  relationship: string | null
  attendanceStatus: "present" | "absent" | "excused"
  notes: string | null
}

interface ParticipantListItem {
  id: string
  code: string
  programId: string
  programName?: string
  firstName: string
  lastName: string
  email: string | null
  cohort: string
  since: string
  attendancePercent: number
  notes: string | null
  status: ParticipantStatus
}

const STATUS_COLORS: Record<ParticipantStatus, { bg: string; text: string }> = {
  active: { bg: "bg-green-100", text: "text-green-700" },
  inactive: { bg: "bg-gold-100", text: "text-gold-700" },
  graduated: { bg: "bg-blue-100", text: "text-blue-700" },
}

function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  )
}

function ScissorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <line x1="20" y1="4" x2="8.12" y2="15.88" />
      <line x1="14.47" y1="14.48" x2="20" y2="20" />
      <line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
  )
}

function AtomIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" />
      <path d="M20.2 20.2c2.04-2.03.02-7.36-4.5-11.9-4.54-4.52-9.87-6.54-11.9-4.5-2.04 2.03-.02 7.36 4.5 11.9 4.54 4.52 9.87 6.54 11.9 4.5Z" />
      <path d="M15.7 15.7c4.52-4.54 6.54-9.87 4.5-11.9-2.03-2.04-7.36-.02-11.9 4.5-4.52 4.54-6.54 9.87-4.5 11.9 2.03 2.04 7.36.02 11.9-4.5Z" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

function CalendarPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 13V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="19" y1="16" x2="19" y2="22" />
      <line x1="16" y1="19" x2="22" y2="19" />
    </svg>
  )
}

function ProgramIcon({ icon, className }: { icon: ProgramIcon; className?: string }) {
  switch (icon) {
    case "book":
      return <BookIcon className={className} />
    case "scissor":
      return <ScissorIcon className={className} />
    case "atom":
      return <AtomIcon className={className} />
    default:
      return <BookIcon className={className} />
  }
}

function ProgramCard({
  program,
  isHighlighted,
  onClick,
  onNewEvent,
  onManageParticipants,
}: {
  program: ProgramListItem
  isHighlighted: boolean
  onClick: () => void
  onNewEvent: () => void
  onManageParticipants: () => void
}) {
  return (
    <div
      onClick={onClick}
      className={`cursor-pointer rounded-[20px] border p-5 transition-colors ${
        isHighlighted
          ? "border-transparent bg-cream-100"
          : "border-gold-500/30 bg-white hover:bg-cream-50"
      }`}
    >
      <div className="flex items-center justify-between">
        <ProgramIcon icon={program.icon} className="h-6 w-6 text-ink" />
        <div className="text-right text-sm text-ink/50">
          <div>{program.participantsCount} program participants</div>
          <div>{program.eventParticipantsCount} event attendance records</div>
        </div>
      </div>
      <div className="mt-6 space-y-2">
        <h3 className="text-xl font-semibold text-ink">{program.name}</h3>
        <p className="text-sm text-ink/70">{program.description}</p>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation()
            onManageParticipants()
          }}
          className="inline-flex items-center gap-2 rounded-full bg-forest-950 px-4 py-2 text-sm text-white hover:bg-forest-900"
        >
          Manage participants
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onNewEvent()
          }}
          className="inline-flex items-center gap-2 rounded-full border border-gold-500/50 bg-white px-4 py-2 text-sm text-ink hover:bg-cream-50"
        >
          <PlusIcon className="h-4 w-4" />
          New event
        </button>
      </div>
    </div>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

type EventType = "workshop" | "showcase" | "graduation" | "field_trip" | "other"
type AudienceType = "participants_guardians" | "participants_only" | "public" | "staff_only"

const EVENT_TYPES: { value: EventType; label: string }[] = [
  { value: "workshop", label: "Workshop" },
  { value: "showcase", label: "Showcase" },
  { value: "graduation", label: "Graduation" },
  { value: "field_trip", label: "Field trip" },
  { value: "other", label: "Other" },
]

const AUDIENCE_TYPES: { value: AudienceType; label: string }[] = [
  { value: "participants_guardians", label: "Participants + guardians" },
  { value: "participants_only", label: "Participants only" },
  { value: "public", label: "Public" },
  { value: "staff_only", label: "Staff only" },
]

interface Speaker {
  name: string
  role: string
}

function NewEventModal({
  isOpen,
  onClose,
  programId,
  programName,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  programId: string
  programName: string
  onSuccess: () => void
}) {
  const [title, setTitle] = useState("")
  const [eventType, setEventType] = useState<EventType>("workshop")
  const [date, setDate] = useState("")
  const [startTime, setStartTime] = useState("10:00")
  const [endTime, setEndTime] = useState("")
  const [venue, setVenue] = useState("")
  const [audience, setAudience] = useState<AudienceType>("participants_guardians")
  const [description, setDescription] = useState("")
  const [speakers, setSpeakers] = useState<Speaker[]>([{ name: "", role: "" }])
  const [publishStatus, setPublishStatus] = useState<"draft" | "published">("draft")
  const [bannerFile, setBannerFile] = useState<UploadedFile | null>(null)
  const [bannerFileName, setBannerFileName] = useState("")
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string | null>(null)
  const [bannerUploading, setBannerUploading] = useState(false)
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    return () => {
      if (bannerPreviewUrl) URL.revokeObjectURL(bannerPreviewUrl)
    }
  }, [bannerPreviewUrl])

  const handleAddSpeaker = () => {
    setSpeakers([...speakers, { name: "", role: "" }])
  }

  const handleRemoveSpeaker = (index: number) => {
    setSpeakers(speakers.filter((_, i) => i !== index))
  }

  const handleSpeakerChange = (index: number, field: "name" | "role", value: string) => {
    const updated = [...speakers]
    const speaker = updated[index]
    if (speaker) {
      speaker[field] = value
      setSpeakers(updated)
    }
  }

  const resetForm = () => {
    setTitle("")
    setEventType("workshop")
    setDate("")
    setStartTime("10:00")
    setEndTime("")
    setVenue("")
    setAudience("participants_guardians")
    setDescription("")
    setSpeakers([{ name: "", role: "" }])
    setPublishStatus("draft")
    setBannerFile(null)
    setBannerFileName("")
    setBannerPreviewUrl(null)
    setBannerUploadProgress(0)
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      // Filter out empty speakers
      const validSpeakers = speakers.filter(s => s.name.trim() && s.role.trim())

      const res = await client.api.admin.programs.events.$post({
        json: {
          programId,
          title,
          type: eventType,
          date,
          startTime,
          endTime: endTime || undefined,
          venue: venue || undefined,
          audience,
          speakers: validSpeakers.length > 0 ? validSpeakers : undefined,
          bannerKey: bannerFile?.key,
          description: description || undefined,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }

      // If user wants to publish immediately
      if (publishStatus === "published" && "data" in body) {
        const eventId = body.data.id
        const publishRes = await client.api.admin.programs.events[":id"].publish.$post({
          param: { id: eventId },
        })
        const publishBody = await publishRes.json()
        if (!publishRes.ok) {
          setError(getErrorMessage(publishBody, "Event was created but could not be published"))
          return
        }
      }

      onSuccess()
      onClose()
      resetForm()
    } catch {
      setError("Failed to create event")
    } finally {
      setSaving(false)
    }
  }

  const handleBannerChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file || bannerUploading) return

    setBannerUploading(true)
    setBannerUploadProgress(0)
    setError(null)
    const previewUrl = URL.createObjectURL(file)
    setBannerFileName(file.name)
    setBannerPreviewUrl(previewUrl)
    try {
      const uploaded = await uploadFile(file, "event-banner", { onProgress: setBannerUploadProgress })
      setBannerFile(uploaded)
    } catch (error) {
      setBannerFile(null)
      setBannerFileName("")
      setBannerPreviewUrl(null)
      setBannerUploadProgress(0)
      setError(error instanceof Error ? error.message : "Failed to upload banner")
    } finally {
      setBannerUploading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`New ${programName} event`}
      subtitle="Add banner, speakers and details"
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Banner Upload */}
        <div>
          <label className="block text-sm font-medium text-ink">Banner</label>
          <label htmlFor="program-event-banner" className="relative mt-2 flex h-32 cursor-pointer items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-gold-500/50 bg-cream-50 hover:bg-cream-100">
            <input id="program-event-banner" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleBannerChange} />
            {bannerPreviewUrl ? (
              <>
                <img src={bannerPreviewUrl} alt="Event banner preview" className="h-full w-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-ink/75 px-3 py-2 text-white">
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="truncate">{bannerFileName || bannerFile?.name}</span>
                    {bannerUploading && <span>{bannerUploadProgress}%</span>}
                  </div>
                  {bannerUploading && <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/30"><div className="h-full rounded-full bg-white transition-[width]" style={{ width: `${bannerUploadProgress}%` }} /></div>}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-ink/60"><UploadIcon className="h-5 w-5" />Click to upload event banner</div>
            )}
          </label>
        </div>

        {/* Event Title & Type */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink">Event title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Term 2 tailoring showcase"
              className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <Select
            label="Event type"
            value={EVENT_TYPES.find(t => t.value === eventType)?.label || "Workshop"}
            onChange={(label) => {
              const type = EVENT_TYPES.find(t => t.label === label)
              if (type) setEventType(type.value)
            }}
            options={EVENT_TYPES.map(t => t.label)}
          />
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-3 gap-4">
          <DatePicker
            label="Date"
            value={date}
            onChange={setDate}
            min={new Date()}
          />
          <TimePicker
            label="Starts"
            value={startTime}
            onChange={setStartTime}
          />
          <TimePicker label="Ends" value={endTime} onChange={setEndTime} />
        </div>

        {/* Venue */}
        <div>
          <label className="block text-sm font-medium text-ink">Venue</label>
          <input
            type="text"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            placeholder="e.g., Learning Hub, Enugu"
            className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-ink">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="What should attendees know?" className="mt-1 w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none" />
        </div>

        {/* Audience */}
        <Select
          label="Audience"
          value={AUDIENCE_TYPES.find(t => t.value === audience)?.label || "Participants + guardians"}
          onChange={(label) => {
            const type = AUDIENCE_TYPES.find(t => t.label === label)
            if (type) setAudience(type.value)
          }}
          options={AUDIENCE_TYPES.map(t => t.label)}
        />

        {/* Speakers */}
        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-ink">Speakers / facilitators</label>
            <button
              type="button"
              onClick={handleAddSpeaker}
              className="inline-flex items-center gap-1 text-sm text-forest-700 hover:text-forest-900"
            >
              <PlusIcon className="h-4 w-4" />
              Add
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {speakers.map((speaker, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={speaker.name}
                  onChange={(e) => handleSpeakerChange(index, "name", e.target.value)}
                  placeholder="Speaker name"
                  className="flex-1 rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
                <input
                  type="text"
                  value={speaker.role}
                  onChange={(e) => handleSpeakerChange(index, "role", e.target.value)}
                  placeholder="Role / topic"
                  className="flex-1 rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
                {speakers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveSpeaker(index)}
                    className="flex h-10 w-10 items-center justify-center rounded-lg text-ink/50 hover:bg-cream-100 hover:text-ink"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Publish Status */}
        <Select
          label="Publish"
          value={publishStatus === "draft" ? "Draft" : "Published"}
          onChange={(value) => setPublishStatus(value === "Draft" ? "draft" : "published")}
          options={["Draft", "Published"]}
        />

        {error && (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>
        )}

        <ModalActions>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton type="submit" variant="primary" disabled={saving || bannerUploading || !title.trim() || !date}>
            {saving ? "Creating..." : "Create Event"}
          </ModalButton>
        </ModalActions>
      </form>
    </Modal>
  )
}

function AddProgramModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}) {
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [icon, setIcon] = useState<ProgramIcon>("book")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const res = await client.api.admin.programs.$post({
        json: { name, description: description || undefined, icon },
      })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      onSuccess()
      onClose()
      setName("")
      setDescription("")
      setIcon("book")
    } catch {
      setError("Failed to create program")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Program">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            placeholder="e.g., Learning Hub"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            placeholder="Describe the program..."
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Icon</label>
          <div className="flex gap-3">
            {(["book", "scissor", "atom"] as const).map((iconOption) => (
              <button
                key={iconOption}
                type="button"
                onClick={() => setIcon(iconOption)}
                className={`flex h-12 w-12 items-center justify-center rounded-lg border-2 transition-colors ${
                  icon === iconOption
                    ? "border-forest-500 bg-forest-50"
                    : "border-gold-500 bg-white hover:bg-cream-50"
                }`}
              >
                <ProgramIcon icon={iconOption} className="h-6 w-6 text-forest-700" />
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>
        )}

        <ModalActions>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton type="submit" variant="primary" disabled={saving || !name.trim()}>
            {saving ? "Creating..." : "Create Program"}
          </ModalButton>
        </ModalActions>
      </form>
    </Modal>
  )
}

export function ProgramsPage() {
  const navigate = useNavigate()
  const canView = useHasPermission("programs")
  const [programs, setPrograms] = useState<ProgramListItem[]>([])
  const [participants, setParticipants] = useState<ParticipantListItem[]>([])
  const [participantsTotal, setParticipantsTotal] = useState(0)
  const [eventAttendance, setEventAttendance] = useState<EventAttendanceItem[]>([])
  const [eventAttendanceTotal, setEventAttendanceTotal] = useState(0)
  const [participantsPage, setParticipantsPage] = useState(1)
  const [eventAttendancePage, setEventAttendancePage] = useState(1)
  const [activeProgramTab, setActiveProgramTab] = useState<"participants" | "attendance">("participants")
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEventModal, setShowEventModal] = useState(false)

  const fetchPrograms = async () => {
    try {
      const res = await client.api.admin.programs.$get({ query: {} })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        setLoading(false)
        return
      }
      if ("data" in body) {
        const items = body.data.items as ProgramListItem[]
        setPrograms(items)
        // Select first program by default
        const firstItem = items[0]
        if (firstItem && !selectedProgramId) {
          setSelectedProgramId(firstItem.id)
        }
        // If no programs, set loading to false here since fetchParticipants won't run
        if (items.length === 0) {
          setLoading(false)
        }
      }
    } catch {
      setError("Failed to load programs")
      setLoading(false)
    }
  }

  const fetchParticipants = async () => {
    if (!selectedProgramId) {
      setParticipants([])
      setParticipantsTotal(0)
      return
    }
    try {
      const res = await client.api.admin.programs[":id"].participants.$get({
        param: { id: selectedProgramId },
        query: {
          limit: String(PROGRAM_RECORD_PAGE_SIZE),
          offset: String((participantsPage - 1) * PROGRAM_RECORD_PAGE_SIZE),
        },
      })
      const body = await res.json()
      if (!res.ok) {
        return
      }
      if ("data" in body) {
        setParticipants(body.data.items as ParticipantListItem[])
        setParticipantsTotal(body.data.total)
      }
    } catch {
      // Silently fail
    } finally {
      setLoading(false)
    }
  }

  const fetchEventAttendance = async () => {
    if (!selectedProgramId) {
      setEventAttendance([])
      setEventAttendanceTotal(0)
      return
    }
    try {
      const res = await client.api.admin.programs[":id"]["event-participants"].$get({
        param: { id: selectedProgramId },
        query: {
          limit: String(PROGRAM_RECORD_PAGE_SIZE),
          offset: String((eventAttendancePage - 1) * PROGRAM_RECORD_PAGE_SIZE),
        },
      })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setEventAttendance(body.data.items as EventAttendanceItem[])
        setEventAttendanceTotal(body.data.total)
      }
    } catch {
      setEventAttendance([])
    }
  }

  useEffect(() => {
    setActiveProgramTab("participants")
    setParticipantsPage(1)
    setEventAttendancePage(1)
  }, [selectedProgramId])

  useEffect(() => {
    if (canView) {
      void fetchPrograms()
    } else {
      setLoading(false)
    }
  }, [canView])

  useEffect(() => {
    if (canView && programs.length > 0) {
      void fetchParticipants()
      void fetchEventAttendance()
    }
  }, [selectedProgramId, programs, participantsPage, eventAttendancePage, canView])

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Programs</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view programs.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <ProgramsPageSkeleton />
  }

  const selectedProgram = programs.find((p) => p.id === selectedProgramId)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Programs</h1>
          <p className="mt-1 text-sm text-ink/60">
            Cohorts across learning, skills, and women's empowerment.
          </p>
        </div>
        {selectedProgram && (
          <button
            onClick={() => setShowEventModal(true)}
            className="inline-flex items-center gap-2 rounded-full bg-forest-950 px-5 py-3 text-sm font-medium text-white hover:bg-forest-900"
          >
            <CalendarPlusIcon className="h-5 w-5" />
            New {selectedProgram.name} event
          </button>
        )}
      </div>

      {/* Program Cards */}
      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      ) : programs.length === 0 ? (
        <div className="rounded-[20px] border-2 border-dashed border-gold-500 bg-white p-12 text-center">
          <BookIcon className="mx-auto h-12 w-12 text-ink/30" />
          <h3 className="mt-4 text-lg font-semibold text-ink">No programs yet</h3>
          <p className="mt-2 text-sm text-ink/60">
            Get started by creating your first program.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-forest-950 px-5 py-3 text-sm font-medium text-white hover:bg-forest-900"
          >
            <PlusIcon className="h-5 w-5" />
            Create program
          </button>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {programs.map((program) => (
              <ProgramCard
                key={program.id}
                program={program}
                isHighlighted={program.id === selectedProgramId}
                onClick={() => setSelectedProgramId(program.id)}
                onNewEvent={() => {
                  setSelectedProgramId(program.id)
                  setShowEventModal(true)
                }}
                onManageParticipants={() => navigate(`/admin/programs/${program.id}`)}
              />
            ))}
          </div>

          {selectedProgram && (
            <section className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
              <div className="border-b border-gold-500/50 bg-cream-50 px-4 pt-4">
                <div className="flex flex-wrap gap-6" role="tablist" aria-label={`${selectedProgram.name} records`}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeProgramTab === "participants"}
                    onClick={() => setActiveProgramTab("participants")}
                    className={`border-b-2 pb-3 text-sm font-semibold transition-colors ${activeProgramTab === "participants" ? "border-forest-950 text-forest-950" : "border-transparent text-ink/50 hover:text-ink"}`}
                  >
                    Program participants <span className="ml-1 text-xs font-normal">{participantsTotal}</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeProgramTab === "attendance"}
                    onClick={() => setActiveProgramTab("attendance")}
                    className={`border-b-2 pb-3 text-sm font-semibold transition-colors ${activeProgramTab === "attendance" ? "border-forest-950 text-forest-950" : "border-transparent text-ink/50 hover:text-ink"}`}
                  >
                    Event attendance <span className="ml-1 text-xs font-normal">{eventAttendanceTotal}</span>
                  </button>
                </div>
              </div>

              {activeProgramTab === "participants" ? (
                <div key="program-participants-tab" className="animate-fade-in-up motion-reduce:animate-none" style={{ animationDuration: "180ms" }}>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gold-500/50 bg-cream-50">
                        <th className="px-4 py-4 text-left text-sm font-bold text-ink">Participant</th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-ink">Cohort</th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-ink">Since</th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-ink">Attendance</th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-ink">Notes</th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-ink">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink/10">
                      {participants.length === 0 ? (
                        <tr><td colSpan={6} className="px-4 py-8 text-center text-ink/50">No participants in this program yet.</td></tr>
                      ) : participants.map((participant) => (
                        <tr key={participant.id} onClick={() => navigate(`/admin/programs/participants/${participant.id}`)} className="cursor-pointer hover:bg-cream-50">
                          <td className="px-4 py-4"><p className="text-sm font-medium text-ink">{participant.firstName} {participant.lastName}</p>{participant.email && <p className="text-xs text-ink/50">{participant.email}</p>}</td>
                          <td className="px-4 py-4 text-sm text-ink/70">{participant.cohort}</td>
                          <td className="px-4 py-4 text-sm text-ink/70">{participant.since}</td>
                          <td className="px-4 py-4 text-sm text-ink/70">{participant.attendancePercent}%</td>
                          <td className="px-4 py-4 text-sm text-ink">{participant.notes || "-"}</td>
                          <td className="px-4 py-4"><span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${STATUS_COLORS[participant.status].bg} ${STATUS_COLORS[participant.status].text}`}>{participant.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-ink/10 px-4 py-3">
                  <ContentPagination page={participantsPage} pageSize={PROGRAM_RECORD_PAGE_SIZE} total={participantsTotal} onPageChange={setParticipantsPage} />
                </div>
                </div>
              ) : (
                <div key="event-attendance-tab" className="animate-fade-in-up motion-reduce:animate-none" style={{ animationDuration: "180ms" }}>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead>
                      <tr className="border-b border-gold-500/50 bg-cream-50">
                        <th className="px-4 py-4 text-left text-sm font-bold text-ink">Event</th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-ink">Participant</th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-ink">Age / Gender</th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-ink">Contact</th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-ink">Attendance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink/10">
                      {eventAttendance.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-ink/50">No event attendance logged for this program yet.</td></tr>
                      ) : eventAttendance.map((item) => (
                        <tr key={item.id} className="cursor-pointer hover:bg-cream-50" onClick={() => navigate(`/admin/events/${item.eventId}`)}>
                          <td className="px-4 py-4"><p className="text-sm font-medium text-ink">{item.eventTitle}</p><p className="text-xs text-ink/50">{item.eventCode} - {new Date(item.eventDate).toLocaleDateString("en-GB")}</p></td>
                          <td className="px-4 py-4 text-sm text-ink">{item.firstName} {item.lastName}{item.relationship && <span className="block text-xs text-ink/50">{item.relationship}</span>}</td>
                          <td className="px-4 py-4 text-sm text-ink/70">{item.age ?? "-"} / {item.gender?.replaceAll("_", " ") ?? "-"}</td>
                          <td className="px-4 py-4 text-sm text-ink/70">{item.phone || item.email || "-"}</td>
                          <td className="px-4 py-4"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${item.attendanceStatus === "present" ? "bg-green-100 text-green-700" : item.attendanceStatus === "absent" ? "bg-red-100 text-red-700" : "bg-gold-100 text-gold-700"}`}>{item.attendanceStatus}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-ink/10 px-4 py-3">
                  <ContentPagination page={eventAttendancePage} pageSize={PROGRAM_RECORD_PAGE_SIZE} total={eventAttendanceTotal} onPageChange={setEventAttendancePage} />
                </div>
                </div>
              )}
            </section>
          )}
        </>
      )}

      <AddProgramModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => void fetchPrograms()}
      />

      <NewEventModal
        isOpen={showEventModal}
        onClose={() => setShowEventModal(false)}
        programId={selectedProgram?.id || ""}
        programName={selectedProgram?.name || ""}
         onSuccess={() => void Promise.all([fetchPrograms(), fetchParticipants(), fetchEventAttendance()])}
      />
    </div>
  )
}
